// src/belediye/redux/slices/belediyeHalkMasasiSlice.js
//
// Halk Masası akışı için tek slice:
// - Sorgu (telefon -> citizen resolve)
// - Son sorgular (backend recent + local minimal recent)    nameMasked eklendi
// - Profil (citizen + applications list)
// - Başvuru detay (  FEED: timeline+sms kronolojik tek endpoint)
// - Başvuru detay sol panel için   application + citizen summary (state-only, KVKK: storage yok)
// - Yeni başvuru (assisted-create)
// - OTP akışı (pre-submit / resend / verify)
//
// KVKK:
// - localStorage sadece hm_recent + hm_lastCitizenId tutar
// - hm_recent: citizenId + phoneMasked + nameMasked + lastViewedAt (detay yok)
// - Feed/Timeline/SMS içerikleri localStorage'a yazılmaz
// - Personel/actor isimleri zaten UI'da gösterilmiyor (MaskedTimeline)
//
// Debug:
// - REACT_APP_DEBUG_HALK_MASASI=1 iken console log basar
//
// API: src/belediye/services/halkMasasiApi.js

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  resolveCitizenByPhone,
  getRecentCitizenLookups,
  assistedCreateApplication,
  normalizePhone10,
  getApplicationPublicFeed,

  // OTP
  preSubmitOtpAssistedApplication,
  resendAssistedOtp,
  submitVerifiedAssistedApplication,
} from "../../services/halkMasasiApi";

// ------------------------------------------------------------
// Debug
// ------------------------------------------------------------
const DEBUG = String(process.env.REACT_APP_DEBUG_HALK_MASASI || "").trim() === "1";
const log = (...args) => {
  if (DEBUG) console.log("[belediyeHalkMasasiSlice]", ...args);
};
const logError = (...args) => {
  if (DEBUG) console.error("[belediyeHalkMasasiSlice]", ...args);
};

// ------------------------------------------------------------
// Safe storage (SSR/private mode/permission fail-safe)
// ------------------------------------------------------------
const canUseStorage = () => {
  try {
    return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
  } catch {
    return false;
  }
};
const storageGet = (key) => {
  try {
    if (!canUseStorage()) return null;
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};
const storageSet = (key, value) => {
  try {
    if (!canUseStorage()) return;
    window.localStorage.setItem(key, value);
  } catch {
    // ignore
  }
};
const storageRemove = (key) => {
  try {
    if (!canUseStorage()) return;
    window.localStorage.removeItem(key);
  } catch {
    // ignore
  }
};

// ------------------------------------------------------------
// LocalStorage (KVKK kontrollü)
// ------------------------------------------------------------
const LS = {
  recent: "hm_recent",
  lastCitizenId: "hm_lastCitizenId",
};

const MAX_RECENT = 12;

const safeJsonParse = (val, fallback) => {
  try {
    if (!val) return fallback;
    return JSON.parse(val);
  } catch {
    return fallback;
  }
};

// KVKK: mask helpers (citizen için; personel değil)
const maskPhone = (phone10) => {
  const p = String(phone10 || "").replace(/\D+/g, "");
  if (p.length !== 10) return "";
  return `${p.slice(0, 3)}*** **${p.slice(8, 10)}`;
};

const maskNameTr = (name, surname) => {
  const n = String(name || "").trim();
  const s = String(surname || "").trim();

  const maskWord = (w) => {
    const t = String(w || "").trim();
    if (!t) return "";
    return `${t.charAt(0).toLocaleUpperCase?.("tr-TR") ?? t.charAt(0).toUpperCase()}***`;
  };

  const mn = maskWord(n);
  const ms = maskWord(s);

  const full = [mn, ms].filter(Boolean).join(" ").trim();
  return full || "";
};

const readRecentFromStorage = () => {
  const raw = storageGet(LS.recent);
  const arr = safeJsonParse(raw, []);
  if (!Array.isArray(arr)) return [];

  const out = arr
    .map((x) => ({
      citizenId: x?.citizenId ? String(x.citizenId) : "",
      phoneMasked: String(x?.phoneMasked || ""),
      nameMasked: String(x?.nameMasked || ""),
      lastViewedAt: String(x?.lastViewedAt || ""),
    }))
    .filter((x) => x.citizenId && x.lastViewedAt)
    .slice(0, MAX_RECENT);

  log("readRecentFromStorage:", out);
  return out;
};

const writeRecentToStorage = (items) => {
  const safe = (Array.isArray(items) ? items : [])
    .map((x) => ({
      citizenId: x?.citizenId ? String(x.citizenId) : "",
      phoneMasked: String(x?.phoneMasked || ""),
      nameMasked: String(x?.nameMasked || ""),
      lastViewedAt: String(x?.lastViewedAt || ""),
    }))
    .filter((x) => x.citizenId && x.lastViewedAt)
    .slice(0, MAX_RECENT);

  storageSet(LS.recent, JSON.stringify(safe));
  log("writeRecentToStorage:", safe);
};

const setLastCitizenId = (citizenId) => {
  const id = String(citizenId || "").trim();
  if (!id) return;
  storageSet(LS.lastCitizenId, id);
  log("setLastCitizenId:", id);
};

const readLastCitizenId = () => {
  try {
    const v = String(storageGet(LS.lastCitizenId) || "").trim() || null;
    log("readLastCitizenId:", v);
    return v;
  } catch {
    return null;
  }
};

const pushRecentLookupLocal = ({ citizenId, phone10, nameMasked }) => {
  const now = new Date().toISOString();
  const id = String(citizenId || "").trim();
  if (!id) return readRecentFromStorage();

  const maskedPhone = maskPhone(phone10);
  const maskedName = String(nameMasked || "").trim();

  const prev = readRecentFromStorage();

  const next = [
    { citizenId: id, phoneMasked: maskedPhone, nameMasked: maskedName, lastViewedAt: now },
    ...prev.filter((x) => String(x.citizenId) !== id),
  ].slice(0, MAX_RECENT);

  writeRecentToStorage(next);
  setLastCitizenId(id);

  log("pushRecentLookupLocal:", { citizenId: id, phone10, maskedPhone, maskedName, now });
  return next;
};

// Logout/hard reset için dışarıdan çağırmak istersen
export const clearHalkMasasiStorage = () => {
  storageRemove(LS.recent);
  storageRemove(LS.lastCitizenId);
  log("clearHalkMasasiStorage: OK");
};

// ------------------------------------------------------------
// Helpers (response normalize)
// ------------------------------------------------------------
const normalizeResolveResponse = (data) => {
  const citizen = data?.citizen || data?.data?.citizen || data?.result?.citizen || null;
  const apps =
    data?.applications ||
    data?.apps ||
    data?.items ||
    data?.results ||
    data?.data?.applications ||
    [];

  const out = {
    citizen: citizen || null,
    applications: Array.isArray(apps) ? apps : [],
    raw: data,
  };

  log("normalizeResolveResponse:", {
    citizenId: out?.citizen?.id ?? out?.citizen?.citizen_id ?? out?.citizen?.pk ?? null,
    appsCount: out.applications.length,
  });

  return out;
};

const safeTime = (v) => {
  try {
    const t = new Date(v || 0).getTime();
    return Number.isNaN(t) ? 0 : t;
  } catch {
    return 0;
  }
};

const normalizeOtpSessionResponse = (data) => {
  const out = {
    sessionToken: data?.session_token ? String(data.session_token) : null,
    expiresAt: data?.expires_at || null,
    resendAvailableAt: data?.resend_available_at || null,
    smsRemaining: Number(data?.sms_remaining || 0),
    sessionStatus: data?.status || null,
    smsFailed: Boolean(data?.sms_failed),
    raw: data || null,
  };

  log("normalizeOtpSessionResponse:", out);
  return out;
};

const pickCreatedApplicationId = (data) => {
  const id =
    data?.application?.id ??
    data?.application_id ??
    data?.id ??
    null;
  return id != null ? String(id) : null;
};

// --------------------------
// FEED normalize
// --------------------------
const normalizeFeedResponse = (data) => {
  const appId = data?.application_id ?? data?.applicationId ?? data?.id ?? null;

  const rawItems =
    data?.items ||
    data?.results ||
    data?.data?.items ||
    data?.data?.results ||
    [];

  const items = (Array.isArray(rawItems) ? rawItems : [])
    .map((x, idx) => {
      const at = x?.at || x?.created_at || x?.sent_at || x?.date || null;
      if (!at) return null;

      const kindRaw = String(x?.kind || "").trim();
      const kind = kindRaw || (x?.message ? "sms" : "timeline");

      return {
        _k: String(x?.id ?? `${kind}:${at}:${idx}`),
        kind,
        title: String(x?.title || (kind === "sms" ? "SMS" : "Güncelleme") || "").trim(),
        at,
        type: x?.type ?? null,
        meta: x?.meta ?? null,
        citizen_note: x?.citizen_note ?? x?.citizenNote ?? null,
        message: x?.message ?? null,
      };
    })
    .filter(Boolean)
    .filter((x) => x.kind && x.at);

  items.sort((a, b) => {
    const ta = safeTime(a.at);
    const tb = safeTime(b.at);
    if (ta !== tb) return ta - tb;
    return String(a._k).localeCompare(String(b._k));
  });

  const summaryFromRaw = extractSummaryFromFeedRaw(data);
  const applicationFromMeta = extractApplicationFromFeedItemsMeta(items);

  const bestApplication = summaryFromRaw?.application || applicationFromMeta || null;
  const bestCitizen = summaryFromRaw?.citizen || null;

  return {
    applicationId: appId ? String(appId) : null,
    items,
    raw: data,
    summary: {
      application: bestApplication,
      citizen: bestCitizen,
    },
  };
};

// --------------------------
// Application summary helpers (no backend change)
// --------------------------
const pick = (...vals) => vals.find((v) => v !== null && v !== undefined && v !== "");

const normalizeApplicationFromAny = (appLike) => {
  if (!appLike || typeof appLike !== "object") return null;

  const id = pick(appLike?.id, appLike?.pk, appLike?.application_id);
  if (!id) return null;

  return {
    id: String(id),
    tracking_number: pick(appLike?.tracking_number, appLike?.trackingNumber, appLike?.tracking),
    status: pick(appLike?.status, appLike?.application_status),
    subject: pick(appLike?.subject, appLike?.title),
    description: pick(appLike?.description, appLike?.detail),
    address: pick(appLike?.address, appLike?.address_text),
    unit_detail: appLike?.unit_detail || appLike?.unitDetail || null,
    neighborhood_detail: appLike?.neighborhood_detail || appLike?.neighborhoodDetail || null,
    unit_name: pick(
      appLike?.unit_name,
      appLike?.unitName,
      appLike?.unit_detail?.name,
      appLike?.unit?.name
    ),
    neighborhood_name: pick(
      appLike?.neighborhood_name,
      appLike?.neighborhoodName,
      appLike?.neighborhood_detail?.name,
      appLike?.neighborhood?.name
    ),
    created_at: pick(appLike?.created_at, appLike?.createdAt, appLike?.created),
    status_date: pick(appLike?.status_date, appLike?.statusDate),
    raw: appLike,

    assisted_intake_note: pick(
      appLike?.assisted_intake_note,
      appLike?.assistedIntakeNote,
      appLike?.assisted?.intake_note
    ),

    assisted_intake_created_at: pick(
      appLike?.assisted_intake_created_at,
      appLike?.assistedIntakeCreatedAt,
      appLike?.assisted?.created_at
    ),

    unit: appLike?.unit
      ? {
          id: pick(appLike?.unit?.id),
          name: pick(appLike?.unit?.name),
          slug: pick(appLike?.unit?.slug),
        }
      : appLike?.unit_detail
      ? {
          id: pick(appLike?.unit_detail?.id),
          name: pick(appLike?.unit_detail?.name),
          slug: pick(appLike?.unit_detail?.slug),
        }
      : null,

    neighborhood: appLike?.neighborhood
      ? {
          id: pick(appLike?.neighborhood?.id),
          name: pick(appLike?.neighborhood?.name),
        }
      : appLike?.neighborhood_detail
      ? {
          id: pick(appLike?.neighborhood_detail?.id),
          name: pick(appLike?.neighborhood_detail?.name),
        }
      : null,

    images: Array.isArray(appLike?.images) ? appLike.images : [],
    documents: Array.isArray(appLike?.documents) ? appLike.documents : [],
  };
};

const normalizeCitizenFromAny = (citizenLike) => {
  if (!citizenLike || typeof citizenLike !== "object") return null;
  const id = pick(citizenLike?.id, citizenLike?.citizen_id, citizenLike?.pk);
  if (!id) return null;

  return {
    id: String(id),
    name: pick(citizenLike?.name, citizenLike?.first_name, citizenLike?.firstName),
    surname: pick(citizenLike?.surname, citizenLike?.last_name, citizenLike?.lastName),
    phone_number: pick(citizenLike?.phone_number, citizenLike?.phone, citizenLike?.gsm, citizenLike?.mobile),
    email: pick(citizenLike?.email),
    raw: citizenLike,
  };
};

const extractSummaryFromFeedRaw = (raw) => {
  if (!raw || typeof raw !== "object") return { citizen: null, application: null };

  const app =
    raw?.application ||
    raw?.application_detail ||
    raw?.applicationDetail ||
    raw?.data?.application ||
    raw?.data?.application_detail ||
    null;

  const citizen =
    raw?.citizen ||
    raw?.citizen_detail ||
    raw?.citizenDetail ||
    raw?.data?.citizen ||
    raw?.data?.citizen_detail ||
    null;

  return {
    application: normalizeApplicationFromAny(app),
    citizen: normalizeCitizenFromAny(citizen),
  };
};

const extractApplicationFromFeedItemsMeta = (items) => {
  const arr = Array.isArray(items) ? items : [];
  for (const it of arr) {
    const m = it?.meta;
    if (!m || typeof m !== "object") continue;

    const appLike = m?.application || m?.application_detail || m?.applicationDetail || m;
    const norm = normalizeApplicationFromAny(appLike);
    if (norm) return norm;
  }
  return null;
};

// ------------------------------------------------------------
// Thunks
// ------------------------------------------------------------

/**
 * 1) Son sorgular:
 * - backend recent getir
 * - local minimal recent ile beraber state'e koy
 */
export const fetchRecentLookups = createAsyncThunk(
  "belediyeHalkMasasi/fetchRecentLookups",
  async ({ limit = 10 } = {}, { rejectWithValue }) => {
    const rid = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    log("fetchRecentLookups:start", { rid, limit });

    try {
      const server = await getRecentCitizenLookups({ limit });

      const serverItems = Array.isArray(server)
        ? server
        : server?.items || server?.results || server?.data?.items || server?.data?.results || [];

      const result = {
        server: serverItems,
        local: readRecentFromStorage(),
        lastCitizenId: readLastCitizenId(),
      };

      log("fetchRecentLookups:success", {
        rid,
        serverCount: serverItems.length,
        localCount: result.local.length,
        lastCitizenId: result.lastCitizenId,
      });

      return result;
    } catch (err) {
      const status = err?.response?.status;
      const message = err?.message || (status ? `Recent lookups failed (HTTP ${status})` : "Recent lookups failed");

      logError("fetchRecentLookups:error", { rid, status, message, data: err?.response?.data });
      return rejectWithValue(message);
    }
  }
);

/**
 * 2) Telefon ile vatandaş resolve:
 * - 404 -> found:false
 * - success => activeCitizen + applications set, recent/local update
 */
export const lookupCitizenByPhoneThunk = createAsyncThunk(
  "belediyeHalkMasasi/lookupCitizenByPhone",
  async ({ phone }, { rejectWithValue }) => {
    const rid = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    log("lookupCitizenByPhone:start", { rid, phoneRaw: phone });

    try {
      const phone10 = normalizePhone10(phone);
      log("lookupCitizenByPhone:normalized", { rid, phone10 });

      if (!phone10) return rejectWithValue("Geçersiz telefon.");

      const data = await resolveCitizenByPhone(phone10, { returnNullOn404: true });
      if (!data) {
        log("lookupCitizenByPhone:not-found", { rid, phone10 });
        return { found: false, phone10 };
      }

      const norm = normalizeResolveResponse(data);
      const citizenId = norm?.citizen?.id ?? norm?.citizen?.citizen_id ?? norm?.citizen?.pk ?? null;

      const nm = maskNameTr(norm?.citizen?.name, norm?.citizen?.surname);

      const localRecent = citizenId
        ? pushRecentLookupLocal({ citizenId, phone10, nameMasked: nm })
        : readRecentFromStorage();

      const result = {
        found: true,
        phone10,
        citizen: norm.citizen,
        applications: norm.applications,
        localRecent,
        lastCitizenId: readLastCitizenId(),
        raw: norm.raw,
      };

      log("lookupCitizenByPhone:success", {
        rid,
        citizenId: String(citizenId || ""),
        appsCount: result.applications.length,
      });

      return result;
    } catch (err) {
      const status = err?.response?.status;
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        (status ? `Lookup failed (HTTP ${status})` : "Lookup failed");

      logError("lookupCitizenByPhone:error", { rid, status, message, data: err?.response?.data });
      return rejectWithValue(message);
    }
  }
);

/**
 * 3) Profil refresh
 */
export const refreshCitizenProfileThunk = createAsyncThunk(
  "belediyeHalkMasasi/refreshCitizenProfile",
  async ({ citizenId }, { getState, rejectWithValue }) => {
    const rid = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    log("refreshCitizenProfile:start", { rid, citizenId });

    try {
      const state = getState()?.belediyeHalkMasasi;
      const activeCitizen = state?.activeCitizen;

      const phone =
        (activeCitizen && String(activeCitizen?.id) === String(citizenId)
          ? activeCitizen?.phone_number || activeCitizen?.phone
          : null) || null;

      const phone10 = normalizePhone10(phone);
      log("refreshCitizenProfile:phone", { rid, citizenId, phoneRaw: phone, phone10 });

      if (!phone10) {
        return rejectWithValue(
          "Profil yenilenemedi: vatandaşın telefon bilgisi bulunamadı. Lütfen sorgu ekranından tekrar arayın."
        );
      }

      const data = await resolveCitizenByPhone(phone10, { returnNullOn404: false });
      const norm = normalizeResolveResponse(data);

      const cid = norm?.citizen?.id ?? norm?.citizen?.citizen_id ?? norm?.citizen?.pk ?? citizenId;
      const nm = maskNameTr(norm?.citizen?.name, norm?.citizen?.surname);

      const localRecent = cid
        ? pushRecentLookupLocal({ citizenId: cid, phone10, nameMasked: nm })
        : readRecentFromStorage();

      const result = {
        citizen: norm.citizen,
        applications: norm.applications,
        localRecent,
        lastCitizenId: readLastCitizenId(),
      };

      log("refreshCitizenProfile:success", {
        rid,
        cid: String(cid || ""),
        appsCount: result.applications.length,
      });

      return result;
    } catch (err) {
      const status = err?.response?.status;
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        (status ? `Profile refresh failed (HTTP ${status})` : "Profile refresh failed");

      logError("refreshCitizenProfile:error", { rid, status, message, data: err?.response?.data });
      return rejectWithValue(message);
    }
  }
);

/**
 * 4) Başvuru Detay FEED
 */
export const fetchApplicationFeedThunk = createAsyncThunk(
  "belediyeHalkMasasi/fetchApplicationFeed",
  async ({ applicationId }, { rejectWithValue }) => {
    const rid = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    log("fetchApplicationFeed:start", { rid, applicationId });

    try {
      if (!applicationId) return rejectWithValue("applicationId zorunlu");

      const data = await getApplicationPublicFeed(applicationId);

      log("fetchApplicationFeed:raw", {
        rid,
        applicationId: String(applicationId),
        keys: data && typeof data === "object" ? Object.keys(data) : null,
      });

      const norm = normalizeFeedResponse(data);

      return {
        applicationId: String(applicationId),
        feed: norm,
      };
    } catch (err) {
      const status = err?.response?.status;
      const message =
        err?.response?.data?.detail ||
        err?.message ||
        (status ? `Application feed failed (HTTP ${status})` : "Application feed failed");

      logError("fetchApplicationFeed:error", { rid, status, message, data: err?.response?.data });
      return rejectWithValue(message);
    }
  }
);

/**
 * 4.1) Başvuru Detay SOL PANEL ÖZETİ
 */
export const hydrateApplicationSummaryThunk = createAsyncThunk(
  "belediyeHalkMasasi/hydrateApplicationSummary",
  async ({ applicationId }, { getState }) => {
    const rid = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const appId = String(applicationId || "").trim();
    log("hydrateApplicationSummary:start", { rid, applicationId: appId });

    const state = getState()?.belediyeHalkMasasi;

    const raw = state?.applicationDetail?.feed?.raw || null;
    const fromRaw = extractSummaryFromFeedRaw(raw);

    const activeCitizen = normalizeCitizenFromAny(state?.activeCitizen);
    const apps = Array.isArray(state?.citizenApplications) ? state.citizenApplications : [];
    const fromStateApp =
      apps.map(normalizeApplicationFromAny).find((a) => a && String(a.id) === appId) || null;

    const feedItems = Array.isArray(state?.applicationDetail?.feed?.items)
      ? state.applicationDetail.feed.items
      : [];
    const fromMetaApp = extractApplicationFromFeedItemsMeta(feedItems);

    const application = fromRaw?.application || fromStateApp || fromMetaApp || null;
    const citizen = fromRaw?.citizen || activeCitizen || null;

    log("hydrateApplicationSummary:done", {
      rid,
      applicationId: appId,
      gotCitizen: Boolean(citizen?.id),
      gotApplication: Boolean(application?.id),
      sources: {
        rawApp: Boolean(fromRaw?.application),
        stateApp: Boolean(fromStateApp),
        metaApp: Boolean(fromMetaApp),
        rawCitizen: Boolean(fromRaw?.citizen),
        stateCitizen: Boolean(activeCitizen),
      },
    });

    return {
      applicationId: appId,
      summary: { citizen, application },
    };
  }
);

/**
 * 5) Normal assisted create (backward-compatible)
 */
export const createAssistedApplicationThunk = createAsyncThunk(
  "belediyeHalkMasasi/createAssistedApplication",
  async ({ payload }, { rejectWithValue }) => {
    const rid = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    log("createAssistedApplication:start", { rid, keys: Object.keys(payload || {}) });

    try {
      const data = await assistedCreateApplication(payload || {});
      const applicationId = pickCreatedApplicationId(data);

      log("createAssistedApplication:success", { rid, applicationId: String(applicationId || "") });

      return {
        data,
        applicationId,
      };
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const message = detail || err?.message || (status ? `Create failed (HTTP ${status})` : "Create failed");

      logError("createAssistedApplication:error", { rid, status, message, data: err?.response?.data });

      return rejectWithValue({
        message,
        status,
        payload: err?.response?.data || null,
      });
    }
  }
);

/**
 * 6) OTP pre-submit
 * Backend:
 * - OTP kapalıysa normal create response dönebilir
 * - OTP açıksa session summary döner
 */
export const preSubmitOtpAssistedApplicationThunk = createAsyncThunk(
  "belediyeHalkMasasi/preSubmitOtpAssistedApplication",
  async ({ payload }, { rejectWithValue }) => {
    const rid = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    log("preSubmitOtpAssistedApplication:start", { rid, keys: Object.keys(payload || {}) });

    try {
      const data = await preSubmitOtpAssistedApplication(payload || {});
      const applicationId = pickCreatedApplicationId(data);
      const otp = normalizeOtpSessionResponse(data);

      log("preSubmitOtpAssistedApplication:success", {
        rid,
        hasSession: Boolean(otp.sessionToken),
        applicationId,
      });

      return {
        data,
        applicationId,
        otp,
      };
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const message =
        detail ||
        err?.message ||
        (status ? `OTP pre-submit failed (HTTP ${status})` : "OTP pre-submit failed");

      logError("preSubmitOtpAssistedApplication:error", {
        rid,
        status,
        message,
        data: err?.response?.data,
      });

      return rejectWithValue({
        message,
        status,
        payload: err?.response?.data || null,
      });
    }
  }
);

/**
 * 7) OTP resend
 */
export const resendAssistedOtpThunk = createAsyncThunk(
  "belediyeHalkMasasi/resendAssistedOtp",
  async ({ sessionToken }, { rejectWithValue }) => {
    const rid = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    log("resendAssistedOtp:start", { rid, sessionToken });

    try {
      const data = await resendAssistedOtp({ sessionToken });
      const otp = normalizeOtpSessionResponse(data);

      log("resendAssistedOtp:success", {
        rid,
        sessionToken: otp.sessionToken,
        smsRemaining: otp.smsRemaining,
      });

      return {
        data,
        otp,
      };
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const message =
        detail ||
        err?.message ||
        (status ? `OTP resend failed (HTTP ${status})` : "OTP resend failed");

      logError("resendAssistedOtp:error", {
        rid,
        status,
        message,
        data: err?.response?.data,
      });

      return rejectWithValue({
        message,
        status,
        payload: err?.response?.data || null,
      });
    }
  }
);

/**
 * 8) OTP verify + finalize
 */
export const submitVerifiedAssistedApplicationThunk = createAsyncThunk(
  "belediyeHalkMasasi/submitVerifiedAssistedApplication",
  async ({ sessionToken, code }, { rejectWithValue }) => {
    const rid = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    log("submitVerifiedAssistedApplication:start", { rid, sessionToken });

    try {
      const data = await submitVerifiedAssistedApplication({ sessionToken, code });
      const applicationId = pickCreatedApplicationId(data);

      log("submitVerifiedAssistedApplication:success", {
        rid,
        applicationId,
      });

      return {
        data,
        applicationId,
      };
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const message =
        detail ||
        err?.message ||
        (status ? `OTP verify failed (HTTP ${status})` : "OTP verify failed");

      logError("submitVerifiedAssistedApplication:error", {
        rid,
        status,
        message,
        data: err?.response?.data,
      });

      return rejectWithValue({
        message,
        status,
        payload: err?.response?.data || null,
      });
    }
  }
);

// ------------------------------------------------------------
// Initial state factory (avoids shared references)
// ------------------------------------------------------------
const getInitialState = () => ({
  // Recent
  recent: {
    items: readRecentFromStorage(),
    serverItems: [],
    lastCitizenId: readLastCitizenId(),
    loading: false,
    error: null,
    lastFetchedAt: null,
  },

  // Lookup
  lookup: {
    loading: false,
    error: null,
    lastPhone10: "",
    found: null,
  },

  // Profile
  activeCitizen: null,
  citizenApplications: [],
  profile: {
    loading: false,
    error: null,
    lastRefreshedAt: null,
  },

  // Application detail (feed + summary)
  applicationDetail: {
    applicationId: null,
    summary: {
      citizen: null,
      application: null,
    },
    feed: { applicationId: null, items: [], raw: null },
    loading: false,
    error: null,
    lastFetchedAt: null,
    lastHydratedAt: null,
  },

  // Create
  create: {
    loading: false,
    error: null,
    lastCreatedApplicationId: null,
    lastCreatedAt: null,
  },

  // OTP
  otp: {
    sessionToken: null,
    expiresAt: null,
    resendAvailableAt: null,
    smsRemaining: 0,
    sessionStatus: null,
    loading: false,
    verifyLoading: false,
    error: null,
    verifyError: null,
    raw: null,
  },
});

// ------------------------------------------------------------
// Slice
// ------------------------------------------------------------
const belediyeHalkMasasiSlice = createSlice({
  name: "belediyeHalkMasasi",
  initialState: getInitialState(),
  reducers: {
    clearLookupState(state) {
      log("reducer:clearLookupState");
      state.lookup.loading = false;
      state.lookup.error = null;
      state.lookup.found = null;
      state.lookup.lastPhone10 = "";
    },

    clearProfileState(state) {
      log("reducer:clearProfileState");
      state.activeCitizen = null;
      state.citizenApplications = [];
      state.profile.loading = false;
      state.profile.error = null;
      state.profile.lastRefreshedAt = null;
    },

    clearApplicationDetailState(state) {
      log("reducer:clearApplicationDetailState");
      state.applicationDetail = {
        applicationId: null,
        summary: { citizen: null, application: null },
        feed: { applicationId: null, items: [], raw: null },
        loading: false,
        error: null,
        lastFetchedAt: null,
        lastHydratedAt: null,
      };
    },

    clearCreateState(state) {
      log("reducer:clearCreateState");
      state.create.loading = false;
      state.create.error = null;
      state.create.lastCreatedApplicationId = null;
      state.create.lastCreatedAt = null;
    },

    resetOtpState(state) {
      log("reducer:resetOtpState");
      state.otp = {
        sessionToken: null,
        expiresAt: null,
        resendAvailableAt: null,
        smsRemaining: 0,
        sessionStatus: null,
        loading: false,
        verifyLoading: false,
        error: null,
        verifyError: null,
        raw: null,
      };
    },

    clearOtpError(state) {
      log("reducer:clearOtpError");
      state.otp.error = null;
      state.otp.verifyError = null;
    },

    markLastCitizenLocal(state, action) {
      const citizenId = action?.payload?.citizenId;
      log("reducer:markLastCitizenLocal", { citizenId });
      if (!citizenId) return;

      state.recent.lastCitizenId = String(citizenId);
      setLastCitizenId(String(citizenId));
    },

    resetHalkMasasiState(state) {
      log("reducer:resetHalkMasasiState");
      const fresh = getInitialState();
      Object.assign(state, fresh);
      state.recent.items = readRecentFromStorage();
      state.recent.lastCitizenId = readLastCitizenId();
    },
  },
  extraReducers: (builder) => {
    builder
      // -----------------------
      // fetchRecentLookups
      // -----------------------
      .addCase(fetchRecentLookups.pending, (state) => {
        log("extra:fetchRecentLookups.pending");
        state.recent.loading = true;
        state.recent.error = null;
      })
      .addCase(fetchRecentLookups.fulfilled, (state, action) => {
        log("extra:fetchRecentLookups.fulfilled", action.payload);
        const payload = action.payload || {};
        state.recent.serverItems = Array.isArray(payload.server) ? payload.server : [];
        state.recent.items = Array.isArray(payload.local) ? payload.local : readRecentFromStorage();
        state.recent.lastCitizenId = payload.lastCitizenId || readLastCitizenId();
        state.recent.loading = false;
        state.recent.error = null;
        state.recent.lastFetchedAt = new Date().toISOString();
      })
      .addCase(fetchRecentLookups.rejected, (state, action) => {
        logError("extra:fetchRecentLookups.rejected", action.payload || action.error);
        state.recent.loading = false;
        state.recent.error = action.payload || action.error?.message || "Recent lookups failed";
        state.recent.items = readRecentFromStorage();
        state.recent.lastCitizenId = readLastCitizenId();
        state.recent.lastFetchedAt = new Date().toISOString();
      })

      // -----------------------
      // lookupCitizenByPhoneThunk
      // -----------------------
      .addCase(lookupCitizenByPhoneThunk.pending, (state) => {
        log("extra:lookupCitizenByPhone.pending");
        state.lookup.loading = true;
        state.lookup.error = null;
        state.lookup.found = null;
      })
      .addCase(lookupCitizenByPhoneThunk.fulfilled, (state, action) => {
        log("extra:lookupCitizenByPhone.fulfilled", action.payload);
        const p = action.payload || {};
        state.lookup.loading = false;
        state.lookup.error = null;
        state.lookup.lastPhone10 = p.phone10 || "";
        state.lookup.found = p.found === true ? true : p.found === false ? false : null;

        if (!p.found) return;

        state.activeCitizen = p.citizen || null;
        state.citizenApplications = Array.isArray(p.applications) ? p.applications : [];
        state.profile.error = null;
        state.profile.lastRefreshedAt = new Date().toISOString();

        state.recent.items = Array.isArray(p.localRecent) ? p.localRecent : readRecentFromStorage();
        state.recent.lastCitizenId = p.lastCitizenId || readLastCitizenId();
      })
      .addCase(lookupCitizenByPhoneThunk.rejected, (state, action) => {
        logError("extra:lookupCitizenByPhone.rejected", action.payload || action.error);
        state.lookup.loading = false;
        state.lookup.found = null;
        state.lookup.error = action.payload || action.error?.message || "Lookup failed";
      })

      // -----------------------
      // refreshCitizenProfileThunk
      // -----------------------
      .addCase(refreshCitizenProfileThunk.pending, (state) => {
        log("extra:refreshCitizenProfile.pending");
        state.profile.loading = true;
        state.profile.error = null;
      })
      .addCase(refreshCitizenProfileThunk.fulfilled, (state, action) => {
        log("extra:refreshCitizenProfile.fulfilled", action.payload);
        const p = action.payload || {};
        state.profile.loading = false;
        state.profile.error = null;

        state.activeCitizen = p.citizen || state.activeCitizen;
        state.citizenApplications = Array.isArray(p.applications) ? p.applications : [];
        state.profile.lastRefreshedAt = new Date().toISOString();

        if (Array.isArray(p.localRecent)) state.recent.items = p.localRecent;
        state.recent.lastCitizenId = p.lastCitizenId || readLastCitizenId();
      })
      .addCase(refreshCitizenProfileThunk.rejected, (state, action) => {
        logError("extra:refreshCitizenProfile.rejected", action.payload || action.error);
        state.profile.loading = false;
        state.profile.error = action.payload || action.error?.message || "Profile refresh failed";
      })

      // -----------------------
      // fetchApplicationFeedThunk
      // -----------------------
      .addCase(fetchApplicationFeedThunk.pending, (state, action) => {
        log("extra:fetchApplicationFeed.pending", action?.meta?.arg);
        state.applicationDetail.loading = true;
        state.applicationDetail.error = null;

        const requestedId = String(action?.meta?.arg?.applicationId || "").trim();
        if (requestedId && state.applicationDetail.applicationId !== requestedId) {
          state.applicationDetail.applicationId = requestedId;
          state.applicationDetail.feed = { applicationId: requestedId, items: [], raw: null };
          state.applicationDetail.summary = { citizen: null, application: null };
        }
      })
      .addCase(fetchApplicationFeedThunk.fulfilled, (state, action) => {
        const p = action.payload || {};
        state.applicationDetail.loading = false;
        state.applicationDetail.error = null;

        const appId = p.applicationId ? String(p.applicationId) : state.applicationDetail.applicationId;
        state.applicationDetail.applicationId = appId || null;

        const nextFeed = p.feed || {
          applicationId: appId || null,
          items: [],
          raw: null,
          summary: { citizen: null, application: null },
        };
        state.applicationDetail.feed = nextFeed;
        state.applicationDetail.lastFetchedAt = new Date().toISOString();

        const s = nextFeed?.summary || {};
        state.applicationDetail.summary = {
          citizen: s?.citizen || null,
          application: s?.application || null,
        };
        state.applicationDetail.lastHydratedAt = new Date().toISOString();
      })
      .addCase(fetchApplicationFeedThunk.rejected, (state, action) => {
        logError("extra:fetchApplicationFeed.rejected", action.payload || action.error);
        state.applicationDetail.loading = false;
        state.applicationDetail.error = action.payload || action.error?.message || "Application feed failed";
        state.applicationDetail.lastFetchedAt = new Date().toISOString();
      })

      // -----------------------
      // hydrateApplicationSummaryThunk
      // -----------------------
      .addCase(hydrateApplicationSummaryThunk.pending, (state, action) => {
        log("extra:hydrateApplicationSummary.pending", action?.meta?.arg);
      })
      .addCase(hydrateApplicationSummaryThunk.fulfilled, (state, action) => {
        log("extra:hydrateApplicationSummary.fulfilled", action.payload);
        const p = action.payload || {};
        const requestedId = String(p?.applicationId || "").trim();
        if (!requestedId) return;

        if (state.applicationDetail.applicationId && String(state.applicationDetail.applicationId) !== requestedId) {
          log("hydrate ignored (mismatch)", {
            stateAppId: state.applicationDetail.applicationId,
            payloadAppId: requestedId,
          });
          return;
        }

        state.applicationDetail.summary = {
          citizen: p?.summary?.citizen || null,
          application: p?.summary?.application || null,
        };
        state.applicationDetail.lastHydratedAt = new Date().toISOString();
      })
      .addCase(hydrateApplicationSummaryThunk.rejected, (state, action) => {
        logError("extra:hydrateApplicationSummary.rejected", action.payload || action.error);
      })

      // -----------------------
      // createAssistedApplicationThunk
      // -----------------------
      .addCase(createAssistedApplicationThunk.pending, (state) => {
        log("extra:createAssistedApplication.pending");
        state.create.loading = true;
        state.create.error = null;
        state.create.lastCreatedApplicationId = null;
      })
      .addCase(createAssistedApplicationThunk.fulfilled, (state, action) => {
        log("extra:createAssistedApplication.fulfilled", action.payload);
        const p = action.payload || {};
        state.create.loading = false;
        state.create.error = null;
        state.create.lastCreatedApplicationId = p.applicationId || null;
        state.create.lastCreatedAt = new Date().toISOString();

        const createdApp = p?.data?.application || null;
        if (createdApp && createdApp?.id) {
          const exists = (state.citizenApplications || []).some((a) => String(a?.id) === String(createdApp.id));
          if (!exists) state.citizenApplications = [createdApp, ...(state.citizenApplications || [])];
        }
      })
      .addCase(createAssistedApplicationThunk.rejected, (state, action) => {
        logError("extra:createAssistedApplication.rejected", action.payload || action.error);
        state.create.loading = false;
        state.create.error = action.payload || action.error?.message || "Create failed";
      })

      // -----------------------
      // preSubmitOtpAssistedApplicationThunk
      // -----------------------
      .addCase(preSubmitOtpAssistedApplicationThunk.pending, (state) => {
        log("extra:preSubmitOtpAssistedApplication.pending");
        state.otp.loading = true;
        state.otp.error = null;
        state.otp.verifyError = null;
      })
      .addCase(preSubmitOtpAssistedApplicationThunk.fulfilled, (state, action) => {
        log("extra:preSubmitOtpAssistedApplication.fulfilled", action.payload);
        const p = action.payload || {};
        state.otp.loading = false;
        state.otp.error = null;

        if (p?.otp?.sessionToken) {
          state.otp.sessionToken = p.otp.sessionToken;
          state.otp.expiresAt = p.otp.expiresAt;
          state.otp.resendAvailableAt = p.otp.resendAvailableAt;
          state.otp.smsRemaining = p.otp.smsRemaining;
          state.otp.sessionStatus = p.otp.sessionStatus;
          state.otp.raw = p.otp.raw || null;
        } else {
          state.otp.sessionToken = null;
          state.otp.expiresAt = null;
          state.otp.resendAvailableAt = null;
          state.otp.smsRemaining = 0;
          state.otp.sessionStatus = null;
          state.otp.raw = null;
        }

        // OTP kapalıysa backend normal create response döndürebilir
        if (p?.applicationId) {
          state.create.lastCreatedApplicationId = p.applicationId;
          state.create.lastCreatedAt = new Date().toISOString();
        }
      })
      .addCase(preSubmitOtpAssistedApplicationThunk.rejected, (state, action) => {
        logError("extra:preSubmitOtpAssistedApplication.rejected", action.payload || action.error);
        state.otp.loading = false;
        state.otp.error =
          action.payload?.message ||
          action.payload ||
          action.error?.message ||
          "OTP pre-submit failed";
      })

      // -----------------------
      // resendAssistedOtpThunk
      // -----------------------
      .addCase(resendAssistedOtpThunk.pending, (state) => {
        log("extra:resendAssistedOtp.pending");
        state.otp.loading = true;
        state.otp.error = null;
      })
      .addCase(resendAssistedOtpThunk.fulfilled, (state, action) => {
        log("extra:resendAssistedOtp.fulfilled", action.payload);
        const p = action.payload || {};
        state.otp.loading = false;
        state.otp.error = null;

        if (p?.otp) {
          state.otp.sessionToken = p.otp.sessionToken || state.otp.sessionToken;
          state.otp.expiresAt = p.otp.expiresAt;
          state.otp.resendAvailableAt = p.otp.resendAvailableAt;
          state.otp.smsRemaining = p.otp.smsRemaining;
          state.otp.sessionStatus = p.otp.sessionStatus;
          state.otp.raw = p.otp.raw || null;
        }
      })
      .addCase(resendAssistedOtpThunk.rejected, (state, action) => {
        logError("extra:resendAssistedOtp.rejected", action.payload || action.error);
        state.otp.loading = false;
        state.otp.error =
          action.payload?.message ||
          action.payload ||
          action.error?.message ||
          "OTP resend failed";
      })

      // -----------------------
      // submitVerifiedAssistedApplicationThunk
      // -----------------------
      .addCase(submitVerifiedAssistedApplicationThunk.pending, (state) => {
        log("extra:submitVerifiedAssistedApplication.pending");
        state.otp.verifyLoading = true;
        state.otp.verifyError = null;
      })
      .addCase(submitVerifiedAssistedApplicationThunk.fulfilled, (state, action) => {
        log("extra:submitVerifiedAssistedApplication.fulfilled", action.payload);
        const p = action.payload || {};

        state.otp.verifyLoading = false;
        state.otp.verifyError = null;
        state.otp.sessionStatus = "finalized";

        state.create.lastCreatedApplicationId = p.applicationId || null;
        state.create.lastCreatedAt = new Date().toISOString();

        const createdApp = p?.data?.application || null;
        if (createdApp && createdApp?.id) {
          const exists = (state.citizenApplications || []).some((a) => String(a?.id) === String(createdApp.id));
          if (!exists) state.citizenApplications = [createdApp, ...(state.citizenApplications || [])];
        }
      })
      .addCase(submitVerifiedAssistedApplicationThunk.rejected, (state, action) => {
        logError("extra:submitVerifiedAssistedApplication.rejected", action.payload || action.error);
        state.otp.verifyLoading = false;
        state.otp.verifyError =
          action.payload?.message ||
          action.payload ||
          action.error?.message ||
          "OTP verify failed";
      });
  },
});

export const {
  clearLookupState,
  clearProfileState,
  clearApplicationDetailState,
  clearCreateState,
  resetOtpState,
  clearOtpError,
  markLastCitizenLocal,
  resetHalkMasasiState,
} = belediyeHalkMasasiSlice.actions;

export default belediyeHalkMasasiSlice.reducer;

// ------------------------------------------------------------
// Selectors
// ------------------------------------------------------------
export const selectHalkMasasiState = (state) => state?.belediyeHalkMasasi || getInitialState();

// Recent
export const selectRecentLookups = (state) => selectHalkMasasiState(state).recent.items || [];
export const selectRecentServerLookups = (state) => selectHalkMasasiState(state).recent.serverItems || [];
export const selectLastCitizenId = (state) => selectHalkMasasiState(state).recent.lastCitizenId || null;
export const selectRecentLoading = (state) => Boolean(selectHalkMasasiState(state).recent.loading);
export const selectRecentError = (state) => selectHalkMasasiState(state).recent.error || null;

// Lookup
export const selectLookupState = (state) => selectHalkMasasiState(state).lookup;
export const selectLookupLoading = (state) => Boolean(selectLookupState(state)?.loading);
export const selectLookupError = (state) => selectLookupState(state)?.error || null;
export const selectLookupFound = (state) => selectLookupState(state)?.found;
export const selectLookupLastPhone10 = (state) => selectLookupState(state)?.lastPhone10 || "";

// Profile
export const selectActiveCitizen = (state) => selectHalkMasasiState(state).activeCitizen;
export const selectCitizenApplications = (state) => selectHalkMasasiState(state).citizenApplications || [];
export const selectProfileLoading = (state) => Boolean(selectHalkMasasiState(state).profile.loading);
export const selectProfileError = (state) => selectHalkMasasiState(state).profile.error || null;

// Application detail
export const selectApplicationDetailState = (state) => selectHalkMasasiState(state).applicationDetail;
export const selectActiveApplicationId = (state) => selectApplicationDetailState(state).applicationId;

export const selectApplicationDetailLoading = (state) => Boolean(selectApplicationDetailState(state).loading);
export const selectApplicationDetailError = (state) => selectApplicationDetailState(state).error || null;

export const selectApplicationFeed = (state) => selectApplicationDetailState(state).feed?.items || [];
export const selectApplicationFeedItems = (state) => selectApplicationFeed(state);

export const selectApplicationSummary = (state) =>
  selectApplicationDetailState(state).summary || { citizen: null, application: null };
export const selectApplicationSummaryCitizen = (state) => selectApplicationSummary(state).citizen;
export const selectApplicationSummaryApplication = (state) => selectApplicationSummary(state).application;

export const selectActiveApplicationTimeline = (state) =>
  (selectApplicationFeed(state) || []).filter((x) => x?.kind === "timeline");
export const selectActiveApplicationSms = (state) =>
  (selectApplicationFeed(state) || []).filter((x) => x?.kind === "sms");

// Create
export const selectCreateState = (state) => selectHalkMasasiState(state).create;
export const selectCreateLoading = (state) => Boolean(selectCreateState(state).loading);
export const selectCreateError = (state) => selectCreateState(state).error || null;
export const selectLastCreatedApplicationId = (state) => selectCreateState(state).lastCreatedApplicationId;

// OTP
export const selectOtpState = (state) => selectHalkMasasiState(state).otp;
export const selectOtpLoading = (state) => Boolean(selectOtpState(state).loading);
export const selectOtpVerifyLoading = (state) => Boolean(selectOtpState(state).verifyLoading);
export const selectOtpError = (state) => selectOtpState(state).error || null;
export const selectOtpVerifyError = (state) => selectOtpState(state).verifyError || null;
export const selectOtpSessionToken = (state) => selectOtpState(state).sessionToken || null;
export const selectOtpExpiresAt = (state) => selectOtpState(state).expiresAt || null;
export const selectOtpResendAvailableAt = (state) => selectOtpState(state).resendAvailableAt || null;
export const selectOtpSmsRemaining = (state) => Number(selectOtpState(state).smsRemaining || 0);
export const selectOtpSessionStatus = (state) => selectOtpState(state).sessionStatus || null;